import * as Yup from 'yup';

import type { Contact, ContactWireFormat } from '../types/api/Contact/Contact';
import { FormikTel } from '../utils/formikHelpers';
import { NIL_UUID } from '../utils/uuid';
import {
  TestForUndefined,
  YupBoolean,
  YupNullableEmail,
  YupNullableString,
  YupNullableTel,
  YupReference,
  type YupSchemaReferenceType,
  YupSometimesNullableEmail,
  YupSometimesNullableString,
  YupSometimesNullableTel,
  YupString,
} from '../utils/yupHelpers';
import { NewCompanySchema } from './company';

export type ContactFormikType = Omit<ContactWireFormat, 'company'> & {
  company: YupSchemaReferenceType;
};

type TemporaryAny = any;

export type RequiredContactFieldsInfo = {
  fullContactInfo: boolean;
  requiresEmail: boolean;
};

const sharedContactSchemaFields = {
  isArchived: YupBoolean('Archived'),
  firstName: YupString('First name'),
  lastName: YupString('Last name'),
  // These act as the fallbacks for non-dynamic schemas
  jobTitle: YupNullableString('Job title'),
  emailAddress: YupNullableEmail('Email'),
  phoneNumber: YupNullableTel('Phone number'),
};

const contactInfoOverrides = (requiredFields: RequiredContactFieldsInfo) => ({
  jobTitle: YupSometimesNullableString('Job title', requiredFields.fullContactInfo),
  emailAddress: YupSometimesNullableEmail(
    'Email',
    requiredFields.fullContactInfo || requiredFields.requiresEmail,
  ),
  phoneNumber: YupSometimesNullableTel('Phone number', requiredFields.fullContactInfo),
});

export const BaseContactSchema: Yup.SchemaOf<Omit<ContactFormikType, 'id' | 'company' | 'notes'>> =
  Yup.object(sharedContactSchemaFields);

export type ContactSchemaBaseType = Omit<ContactFormikType, 'id'>;

export const NewBaseContactSchema = (
  requiredFields: RequiredContactFieldsInfo,
): Yup.SchemaOf<Omit<ContactFormikType, 'id' | 'company' | 'notes'>> =>
  Yup.object({
    ...sharedContactSchemaFields,
    ...contactInfoOverrides(requiredFields),
  });

export const ContactSchema: Yup.SchemaOf<ContactSchemaBaseType> = Yup.object({
  ...sharedContactSchemaFields,
  notes: YupNullableString('Notes (Internal Only)'),
  company: YupReference('Company'),
}).test(TestForUndefined('ContactSchema'));

export const NewContactSchema = (
  requiredFields: RequiredContactFieldsInfo,
): Yup.SchemaOf<ContactSchemaBaseType> =>
  Yup.object({
    ...sharedContactSchemaFields,
    notes: YupNullableString('Notes (Internal Only)'),
    company: YupReference('Company'),

    // Override with dynamic requirements
    ...contactInfoOverrides(requiredFields),
  }).test(TestForUndefined('ContactSchema'));

export const NewContactSchemaFormik = (
  requiredFields: RequiredContactFieldsInfo,
): Yup.ObjectSchema<ContactSchemaBaseType & TemporaryAny> =>
  // ts-expect-error Other fields will be added later
  Yup.object().shape({
    ...sharedContactSchemaFields,
    notes: YupNullableString('Notes (Internal Only)'),
    company: YupReference('Company'),

    // Override with dynamic requirements
    ...contactInfoOverrides(requiredFields),
  });

export const NewContactCompanySchema = (areMarketsRequired: boolean) =>
  Yup.lazy((value: unknown) => {
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;

      const looksLikeNewCompany =
        'name' in obj ||
        'address' in obj ||
        'category' in obj ||
        'dispatchCustomers' in obj ||
        'markets' in obj ||
        'divisions' in obj;

      if (looksLikeNewCompany) {
        return NewCompanySchema({ markets: areMarketsRequired });
      }
    }

    return YupReference('Company');
  });

export const NewContactAndCompanySchemaFormik = (
  requiredFields: RequiredContactFieldsInfo,
  areMarketsRequired = false,
): Yup.ObjectSchema<ContactSchemaBaseType & TemporaryAny> =>
  Yup.object().shape({
    ...sharedContactSchemaFields,
    notes: YupNullableString('Notes (Internal Only)'),
    company: NewContactCompanySchema(areMarketsRequired),

    // Override with dynamic requirements
    ...contactInfoOverrides(requiredFields),
  });

export const FormikContact = (
  contactData: Partial<Contact> | undefined,
  usesDispatchCustomer: boolean,
): ContactFormikType => ({
  id: contactData?.id ?? NIL_UUID,
  isArchived: contactData?.isArchived ?? false,
  firstName: contactData?.firstName ?? '',
  lastName: contactData?.lastName ?? '',
  emailAddress: contactData?.emailAddress ?? null,
  phoneNumber: FormikTel(contactData?.phoneNumber ?? undefined),
  jobTitle: contactData?.jobTitle ?? null,
  notes: contactData?.notes ?? null,
  company: {
    id: contactData?.company?.id ?? null,
    option:
      contactData?.company === undefined
        ? null
        : {
            value: contactData.company.id,
            label: contactData.company.name,
            sublabels: contactData.company.lookupSublabels(usesDispatchCustomer),
          },
  },
});
